import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import { emptyResourceState, ResourceState } from '@/business/resource/store/resource-state'
import {
  CLEAR_RESOURCE,
  CREATE_RESOURCE,
  CREATE_RESOURCE_VARIANT,
  FETCH_RESOURCE,
  FETCH_RESOURCES,
  REMOVE_RESOURCE,
  REMOVE_RESOURCE__FAILED,
  REMOVE_RESOURCE__HIDE,
  REMOVE_RESOURCE_VARIANT,
  REMOVE_RESOURCE_VARIANT__FAILED,
  REMOVE_RESOURCE_VARIANT__HIDE,
  RESTORE_RESOURCE,
  RESTORE_RESOURCE_VARIANT,
  TEMPORARY_RESOURCE__CREATE,
  TEMPORARY_RESOURCE__CREATE_VARIANT,
  TEMPORARY_RESOURCE__REMOVE,
  TEMPORARY_RESOURCE__REMOVE_VARIANT,
  UPDATE_RESOURCE,
} from '@/business/resource/store/types'
import {
  GetResourceRequest,
  GetResourcesRequest,
  ResourcePageResponse,
  ResourceRequest,
  ResourceResponse,
  ResourceVariant,
} from '@/api/generated'
import { Api } from '@/api/api'
import { emptyPage } from '@/commons/models/empty-page'
import { ResourceViewModel } from '@/business/resource/interfaces/resource-view-model'
import { updateOrAppend, updateOrDiscard, withoutEntry } from '@/shared/utils/list'
import { ID } from '@/shared/utils/id'
import { blobToDataURL } from '@/shared/utils/blob'
import { ExtendedBlob } from '@/shared/models/extended-blob'
import { Loading } from '@/shared/utils/vuex-loading'
import { ResourceType } from '@/business/resource/interfaces/resource-type'
import { ResourceVariantViewModel } from '@/business/resource/interfaces/resource-variant-view-model'

const STORE_NAME = 'resource'

interface CreateTemporaryResource {
  location: string
  name?: string
  temporaryId: string
}

interface CreateTemporaryResourceVariant extends CreateTemporaryResource {
  originalId: number
}

@Module({
  name: STORE_NAME,
})
export class ResourceModule extends VuexModule {
  public static readonly STORE_NAME: string = STORE_NAME

  public data: ResourceState = emptyResourceState()

  public get resources(): ResourceViewModel[] {
    return this.data.resources
  }

  @Mutation
  public cleanup(): void {
    this.data = emptyResourceState()
  }

  @Action({
    commit: CLEAR_RESOURCE,
    rawError: true,
  })
  public clearResources(): void {
    this[CLEAR_RESOURCE]()
  }

  @Loading(CREATE_RESOURCE)
  @Action({
    commit: CREATE_RESOURCE,
    rawError: true,
  })
  public createResource(request: {
    resource: ResourceRequest
    file?: ExtendedBlob
  }): Promise<ResourceViewModel> {
    const temporaryId: string = ID()

    if (request.file !== undefined && request.resource.originalId === undefined) {
      blobToDataURL(request.file).then(location => {
        this[TEMPORARY_RESOURCE__CREATE]({
          location,
          name: request.file?.name,
          temporaryId,
        })
      })
    }

    return Api.Resources.createResource(request).finally(() => {
      if (temporaryId) {
        this[TEMPORARY_RESOURCE__REMOVE](temporaryId)
      }
    })
  }

  @Loading(CREATE_RESOURCE_VARIANT)
  @Action({
    commit: CREATE_RESOURCE_VARIANT,
    rawError: true,
  })
  public createVariant(payload: {
    file: ExtendedBlob
    resource: ResourceRequest & { originalId: number }
  }): Promise<ResourceVariant> {
    const temporaryId: string = ID()

    blobToDataURL(payload.file).then(location => {
      this[TEMPORARY_RESOURCE__CREATE_VARIANT]({
        location,
        name: payload.file?.name,
        originalId: payload.resource.originalId,
        temporaryId,
      })
    })

    return Api.Resources.createResource(payload).finally(() => {
      if (temporaryId) {
        this[TEMPORARY_RESOURCE__REMOVE_VARIANT]({
          originalId: payload.resource.originalId,
          temporaryId,
        })
      }
    })
  }

  @Loading(FETCH_RESOURCE)
  @Action({
    commit: FETCH_RESOURCE,
    rawError: true,
  })
  public fetchResource(request: GetResourceRequest): Promise<ResourceResponse> {
    return Api.Resources.getResource(request)
  }

  @Loading(FETCH_RESOURCES)
  @Action({
    commit: FETCH_RESOURCES,
    rawError: true,
  })
  public fetchResources(request: GetResourcesRequest): Promise<ResourcePageResponse> {
    return Api.Resources.getResources(request).catch(() => emptyPage())
  }

  @Loading(REMOVE_RESOURCE)
  @Action({
    rawError: true,
  })
  public removeResource(resourceId: number): Promise<void> {
    // Hide the resource immediately
    this[REMOVE_RESOURCE__HIDE](resourceId)

    return Api.Resources.deleteResource({
      resourceId,
    })
      .then(() => {
        // The request worked so let's remove the resource
        this[REMOVE_RESOURCE](resourceId)
      })
      .catch(e => {
        // The request failed so let's show the resource
        this[REMOVE_RESOURCE__FAILED](resourceId)

        throw e
      })
  }

  @Loading(REMOVE_RESOURCE_VARIANT)
  @Action({
    rawError: true,
  })
  public removeVariant(payload: { id: number; originalId: number }): Promise<void> {
    // Hide the resource immediately
    this[REMOVE_RESOURCE_VARIANT__HIDE]({
      originalId: payload.originalId,
      id: payload.id,
    })

    return Api.Resources.deleteResource({
      resourceId: payload.id,
    })
      .then(() => {
        // The request worked so let's remove the resource
        this[REMOVE_RESOURCE_VARIANT](payload)
      })
      .catch(e => {
        // The request failed so let's show the resource
        this[REMOVE_RESOURCE_VARIANT__FAILED](payload)

        throw e
      })
  }

  @Loading(RESTORE_RESOURCE)
  @Action({
    rawError: true,
  })
  public async restoreResource(payload: { id: number }): Promise<void> {
    const restoredResource = await Api.Resources.restoreResource({
      resourceId: payload.id,
    })

    this[RESTORE_RESOURCE](restoredResource)
  }

  @Loading(RESTORE_RESOURCE_VARIANT)
  @Action({
    rawError: true,
  })
  public async restoreResourceVariant(payload: { id: number; originalId: number }): Promise<void> {
    const restoredResource = await Api.Resources.restoreResource({
      resourceId: payload.id,
    })

    this[RESTORE_RESOURCE_VARIANT]({
      originalId: payload.originalId,
      resource: restoredResource,
    })
  }

  @Loading(UPDATE_RESOURCE)
  @Action({
    commit: UPDATE_RESOURCE,
    rawError: true,
  })
  public updateResource(resource: ResourceRequest): Promise<ResourceResponse> {
    return Api.Resources.updateResource({
      resource,
      resourceId: resource.id!,
    })
  }

  @Mutation
  public [CLEAR_RESOURCE](): void {
    this.data.resources = []
  }

  @Mutation
  public [CREATE_RESOURCE](resource: ResourceViewModel): void {
    if (resource.originalId === undefined) {
      this.data.resources.push(resource)
    }
  }

  @Mutation
  public [CREATE_RESOURCE_VARIANT](resource: ResourceResponse): void {
    this.data.resources = updateVariantModel(this.data.resources, resource.originalId!, {
      description: resource.description,
      id: resource.id,
      location: resource.location,
      type: resource.type,
    })
  }

  @Mutation
  public [FETCH_RESOURCE](resource: ResourceViewModel): void {
    this.data.resource = resource
  }

  @Mutation
  public [FETCH_RESOURCES](resources: ResourcePageResponse): void {
    this.data.resources = resources.items
  }

  @Mutation
  public [REMOVE_RESOURCE](resourceId: number): void {
    this.data.resources = withoutEntry(this.data.resources, { id: resourceId })
  }

  @Mutation
  public [REMOVE_RESOURCE_VARIANT](payload: { id: number; originalId: number }): void {
    const resource = this.data.resources.find(resource => resource.id === payload.originalId)

    if (resource != null) {
      this.data.resources = updateOrDiscard(this.data.resources, {
        ...resource,
        variants: withoutEntry(resource.variants ?? [], {
          id: payload.id,
        }),
      })
    }
  }

  @Mutation
  public [REMOVE_RESOURCE__FAILED](resourceId: number): void {
    this.data.resources = updateResourceModel(this.data.resources, resourceId, {
      type: ResourceType.FILE,
    })
  }

  @Mutation
  public [REMOVE_RESOURCE__HIDE](resourceId: number): void {
    this.data.resources = updateResourceModel(this.data.resources, resourceId, {
      type: ResourceType.TEMPORARY,
    })
  }

  @Mutation
  public [REMOVE_RESOURCE_VARIANT__FAILED](payload: { id: number; originalId: number }): void {
    this.data.resources = updateVariantModel(this.data.resources, payload.originalId, {
      type: ResourceType.FILE,
      id: payload.id,
    })
  }

  @Mutation
  public [REMOVE_RESOURCE_VARIANT__HIDE](payload: { id: number; originalId: number }): void {
    this.data.resources = updateVariantModel(this.data.resources, payload.originalId, {
      type: ResourceType.TEMPORARY,
      id: payload.id,
    })
  }

  @Mutation
  public [RESTORE_RESOURCE](resource: ResourceResponse): void {
    this.data.resources = updateOrAppend(this.data.resources, resource)
  }

  @Mutation
  public [RESTORE_RESOURCE_VARIANT](payload: {
    originalId: number
    resource: ResourceResponse
  }): void {
    this.data.resources = updateVariantModel(this.data.resources, payload.originalId, {
      description: payload.resource.description,
      id: payload.resource.id,
      location: payload.resource.location,
      type: payload.resource.type,
    })
  }

  @Mutation
  public [TEMPORARY_RESOURCE__CREATE]({
    location,
    name,
    temporaryId,
  }: CreateTemporaryResource): void {
    this.data.resources.push({
      id: -1,
      location,
      name: name || 'Temp',
      temporaryId,
      type: ResourceType.TEMPORARY,
    })
  }

  @Mutation
  public [TEMPORARY_RESOURCE__CREATE_VARIANT]({
    location,
    originalId,
    temporaryId,
  }: CreateTemporaryResourceVariant): void {
    const resource = this.data.resources.find(resource => resource.id === originalId)

    if (resource != null) {
      this.data.resources = updateOrDiscard(this.data.resources, {
        ...resource,
        variants: updateOrAppend(resource.variants ?? [], {
          id: -1,
          location,
          temporaryId,
          type: ResourceType.TEMPORARY,
        }),
      })
    }
  }

  @Mutation
  public [TEMPORARY_RESOURCE__REMOVE](temporaryId: string): void {
    this.data.resources = withoutEntry(
      this.data.resources,
      { temporaryId },
      (a, b) => a.temporaryId === b.temporaryId
    )
  }

  @Mutation
  public [TEMPORARY_RESOURCE__REMOVE_VARIANT](payload: {
    originalId: number
    temporaryId: string
  }): void {
    const resource = this.data.resources.find(resource => resource.id === payload.originalId)

    if (resource != null) {
      this.data.resources = updateOrDiscard(this.data.resources, {
        ...resource,
        variants: withoutEntry(
          resource.variants ?? [],
          { temporaryId: payload.temporaryId },
          (a, b) => a.temporaryId === b.temporaryId
        ),
      })
    }
  }

  @Mutation
  public [UPDATE_RESOURCE](resource: ResourceResponse): void {
    this.data.resources = updateOrDiscard(this.data.resources, resource)
  }
}

function updateResourceModel(
  resources: ResourceViewModel[],
  resourceId: number,
  updates: Partial<ResourceViewModel>
): ResourceViewModel[] {
  const resource = resources.find(_ => _.id === resourceId)

  if (resource) {
    // Since vue cannot detect object changes (see https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats for more information)
    // we need to replace the object
    resources = updateOrDiscard(resources, {
      ...resource,
      ...updates,
    })
  }

  return resources
}

function updateVariantModel(
  resources: ResourceViewModel[],
  originalId: number,
  updates: Partial<ResourceVariantViewModel> & { id: number }
): ResourceViewModel[] {
  const resource = resources.find(resource => resource.id === originalId)

  if (resource != null) {
    const variant = resource.variants?.find(_ => _.id === updates.id)

    if (variant != null) {
      resources = updateOrDiscard(resources, {
        ...resource,
        variants: updateOrDiscard(resource.variants ?? [], {
          ...variant,
          ...updates,
        }),
      })
    } else {
      resources = updateOrDiscard(resources, {
        ...resource,
        variants: updateOrAppend(resource.variants ?? [], {
          ...(updates as ResourceVariantViewModel),
        }),
      })
    }
  }

  return resources
}
